<template>
  <v-col v-if="updateDisplay" class="app-sidebar-wrapper-col" :class="{ collapsed: isLinkMenuCollapsed }">
    <div class="app-sidebar-wrapper">
      <div class="topic">
        <v-list>
          <div
            v-for="item in items"
            :id="`menu-${item.topic}`"
            :key="item.icon"
            class="topic-wrapper"
            :class="{ active: (topic === item.topic) && !isLinkMenuCollapsed }"
            @click="updateTopic(item, false)"
          >
            <div class="topic-logo" :class="topicLogos(item.topic)" :title="$t(item.text)" />
          </div>
        </v-list>
      </div>
      <div class="menu" :class="{ collapsed: isLinkMenuCollapsed }">
        <v-list-item-group color="primary" mandatory>
          <v-list dark dense>
            <div class="topic-name">{{ $t(currentItem.text) }}</div>
            <v-list-item
              v-for="child in currentItem.children"
              :id="`menu-${currentItem.topic}-${child.id}`"
              :key="child.text"
              :value="child.url"
              :ripple="false"
              class="app-sidebar-item"
            >
              <div class="app-sidebar-item-wrapper" :class="{ active: isGroupActive(child.groupName) || isLinkActive(child.url) }" @click="updateURL(child.url)">
                <v-list-item-icon class="ma-2">
                  <v-icon v-if="!child.icon.includes('custom')" :title="$t(child.text)">{{ child.icon }} </v-icon>
                  <div v-else :class="isGroupActive(child.groupName) || isLinkActive(child.url) ? `${child.icon}-active` : child.icon" :title="$t(child.text)" />
                </v-list-item-icon>
                <v-list-item-title>{{ $t(child.text) }}</v-list-item-title>
              </div>
              <div class="app-sidebar-subitem-wrapper">
                <v-list-item
                  v-for="greatChild in child.children"
                  :id="`menu-${currentItem.topic}-${child.id}-${greatChild.id}`"
                  :key="greatChild.text"
                  :class="{ active: isLinkActive(greatChild.url) }"
                  :value="greatChild.url"
                  :ripple="false"
                  class="app-sidebar-subitem"
                  @click="updateURL(greatChild.url)"
                >
                  <v-list-item-title class="pa-0">{{ $t(greatChild.text) }}</v-list-item-title>
                </v-list-item>
              </div>
            </v-list-item>
          </v-list>
        </v-list-item-group>
      </div>
    </div>
  </v-col>
</template>

<script setup lang="ts">
import router, { getCurrentPath, getCurrentTopic } from '@/router/index';
import useStore from '@/utils/store/common';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import links from './links/sidebarLinks';

const route = useRoute();
const store = useStore();

let backgroundTopic = '';
const display = ref(false);
const isLinkMenuCollapsed = ref(false);
const topic = ref(getCurrentTopic());

function getCurrentUserIsAbleToWrite(featureName: string) {
  return featureName ? store.getters['permissions/getCurrentUserIsAbleTo']('write', featureName) : false;
}

function getCurrentUserIsAbleToRead(featureName: string) {
  return featureName ? store.getters['permissions/getCurrentUserIsAbleTo']('read', featureName) || getCurrentUserIsAbleToWrite(featureName) : false;
}

function filterItemCurrentUserIsAbleToRead(item: any) {
  if (item.children?.filter(filterItemCurrentUserIsAbleToRead).length) {
    item.children = item.children.filter(filterItemCurrentUserIsAbleToRead);
    return true;
  }
  return getCurrentUserIsAbleToRead(item.permission_id);
}

const items = computed(() => links.filter(filterItemCurrentUserIsAbleToRead));
const currentItem = computed(() => items.value.find((item: any) => item.topic === topic.value));
const updateDisplay = computed(() => display.value && route.path !== '/permissions-errors' && items.value);

function isLinkActive(url: string) {
  return url.includes(getCurrentPath());
}

function isGroupActive(groupName: string) {
  return getCurrentPath().includes(groupName);
}

function updateURL(url: string) {
  if (url !== getCurrentPath()) {
    router.push({
      path: url
    });
  }
}

function updateTopic(item: any, fromLink: boolean) {
  if (fromLink || (isLinkMenuCollapsed.value === false && topic.value === item.topic)) {
    isLinkMenuCollapsed.value = true;
  } else {
    isLinkMenuCollapsed.value = false;
  }
  topic.value = item.topic;

  // const redirectURL = item.children.find((child: any) => child.url).url;
  // if (redirectURL) {
  //   const [activeTopicName, redirectTopicName] = [getCurrentPath().split('/')[1], redirectURL.split('/')[1]];
  //   if (activeTopicName !== redirectTopicName) {
  //     this.updateURL(redirectURL);
  //   }
  // }
}

function topicLogos(itemTopic: string) {
  return itemTopic === backgroundTopic ? itemTopic : `${itemTopic}-idle`;
}

watch(
  () => route,
  () => {
    const { path } = router.currentRoute;
    display.value = false;
    const topicName = path.split('/')[1] || '';
    if (route.path !== '/permissions-errors' && items) {
      const newItem = items.value.find(item => item.topic === topicName);
      if (newItem) {
        updateTopic(newItem, true);
        topic.value = topicName;
        backgroundTopic = topicName;
        display.value = true;
      }
    }
  },
  {
    immediate: true,
    deep: true
  }
);
</script>
<style lang="scss" scoped>
@import './styles/application-sidebar.scss';
</style>
